import { useCallback, useState } from "react";
import { UUID } from "crypto";
import { useGetConnectionById, useEnrichLeadContact } from "api/enrich";
import { useUpdateLeadCapture } from "api/useUpdateLeadCapture";
import { useCustomToast } from "hooks/useCustomToast";
import {
  enrichMessages,
  enrichErrorMessages,
  EnrichStatus,
} from "constants/const";
import type {
  LeadCapture,
  EnrichStatusType,
  EnrichLeadCaptureContact,
} from "api/enrich";
import type { IContact } from "../types/Contact";
import { AxiosError } from "axios";

const ENRICHMENT_POLL_INTERVAL = 4000;

interface EnrichResponse {
  message: string;
  data: EnrichLeadCaptureContact;
}

const isEnrichmentPending = (status?: EnrichStatusType): boolean => {
  return (
    status === EnrichStatus.IN_PROGRESS || status === EnrichStatus.NOT_STARTED
  );
};

const getEnrichmentMessage = (
  status: EnrichStatusType,
  contact: IContact,
): string => {
  if (status === EnrichStatus.COMPLETED) {
    return contact.enrich_data?.info_found
      ? enrichMessages.ALREADY_ENRICHED
      : enrichMessages.INFO_NOT_FOUND;
  }
  return enrichMessages[status] || enrichMessages.FAILED;
};

// Convert IContact to LeadCapture
const convertToLeadCapture = (contact: IContact) => {
  return {
    ...contact,
    full_name: contact.info.full_name,
    emails: [contact.info.email],
  };
};

export const useContactEnrichment = (
  contactId: string,
  refetchContactsData: () => void,
  handleOutOfCredits: () => void,
) => {
  const toast = useCustomToast();
  const [isPolling, setIsPolling] = useState(false);
  const { mutate: updateExchangeContact, isLoading: isUpdating } =
    useUpdateLeadCapture();
  const { mutate: enrichContact, isLoading: isEnriching } =
    useEnrichLeadContact();

  const handleEnrichmentSuccess = useCallback(
    (response: LeadCapture) => {
      if (!isEnrichmentPending(response.enrich_status)) {
        setIsPolling(false);
      }

      const shouldNotify =
        !response.enrich_notification &&
        response?.enrich_status &&
        !isEnrichmentPending(response.enrich_status);

      if (shouldNotify) {
        const message =
          response.enrich_data?.info_found === false
            ? enrichMessages.INFO_NOT_FOUND
            : enrichMessages[response.enrich_status || EnrichStatus.FAILED];

        toast({ message });
        refetchContactsData();

        updateExchangeContact({
          data: { enrich_notification: true },
          leadCaptureId: response.uuid as UUID,
        });
      }
    },
    [toast, refetchContactsData, updateExchangeContact],
  );

  const { isLoading: isPollingLoading } = useGetConnectionById(contactId, {
    query: {
      enabled: isPolling,
      refetchInterval: ENRICHMENT_POLL_INTERVAL,
      onSuccess: handleEnrichmentSuccess,
    },
  });

  const handleEnrichContact = useCallback(
    (contact: IContact) => {
      const enrichStatus = contact?.enrich_status || EnrichStatus.NOT_STARTED;

      if (enrichStatus === EnrichStatus.NOT_STARTED) {
        enrichContact(contactId, {
          onSuccess: (response: EnrichResponse) => {
            toast({ message: response.message });
            setIsPolling(true);
            refetchContactsData();
          },
          onError: (error: AxiosError<{ message?: string }>) => {
            if (error.response?.status === 402) {
              handleOutOfCredits();
              return;
            }
            const errorMessage =
              typeof error === "string"
                ? error
                : error.response?.data?.message ||
                  error.message ||
                  enrichErrorMessages.FAILED;

            toast({ message: errorMessage });
          },
        });
        return;
      }

      toast({ message: getEnrichmentMessage(enrichStatus, contact) });
    },
    [enrichContact, toast, contactId],
  );

  const handleEnrichClick = useCallback(
    (contact: IContact) => {
      handleEnrichContact(convertToLeadCapture(contact));
    },
    [handleEnrichContact],
  );

  return {
    handleEnrichClick,
    isLoading: isEnriching || isUpdating || isPollingLoading,
  };
};
